/**
 * Health Check API Client
 *
 * Utilities for fetching health status from services.
 * Follows RFC draft-inadarei-api-health-check-06 response format.
 */

export type HealthStatus = 'pass' | 'fail' | 'warn';

export interface HealthCheckResult {
  id: string;
  name: string;
  status: HealthStatus;
  timestamp: string;
  duration?: number;
  observedValue?: any;
  observedUnit?: string;
  output?: string;
  critical?: boolean;
  category?: string;
}

export interface HealthCheckSummary {
  total: number;
  passed: number;
  failed: number;
  warned: number;
}

export interface HealthCheckResponse {
  status: HealthStatus;
  version?: string;
  serviceId?: string;
  timestamp: string;
  checks?: Record<string, HealthCheckResult[]>;
  summary?: HealthCheckSummary;
}

export interface ServiceHealthStatus {
  serviceId: string;
  serviceName: string;
  status: HealthStatus | 'unknown';
  lastChecked: string;
  response?: HealthCheckResponse;
  error?: string;
  loading?: boolean;
}

/**
 * Fetch health status from a service
 *
 * @param url - Health endpoint URL
 * @param timeout - Request timeout in milliseconds (default: 5000)
 * @returns Health check response or error
 */
export async function fetchServiceHealth(
  url: string,
  timeout: number = 5000
): Promise<HealthCheckResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Parse response even if status is 503 (service unhealthy but responding)
    const data: HealthCheckResponse = await response.json();

    return data;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error(`Health check timed out after ${timeout}ms`);
      }
      throw new Error(`Failed to fetch health: ${error.message}`);
    }

    throw new Error('Failed to fetch health: Unknown error');
  }
}

/**
 * Fetch health status for all services
 *
 * @param endpoints - Array of service health URLs
 * @returns Map of service health statuses
 */
export async function fetchAllServiceHealth(
  endpoints: Array<{ serviceId: string; serviceName: string; healthUrl: string }>
): Promise<ServiceHealthStatus[]> {
  const results = await Promise.allSettled(
    endpoints.map(async (endpoint) => {
      try {
        const response = await fetchServiceHealth(endpoint.healthUrl);

        return {
          serviceId: endpoint.serviceId,
          serviceName: endpoint.serviceName,
          status: response.status,
          lastChecked: new Date().toISOString(),
          response,
          loading: false
        };
      } catch (error) {
        return {
          serviceId: endpoint.serviceId,
          serviceName: endpoint.serviceName,
          status: 'unknown' as const,
          lastChecked: new Date().toISOString(),
          error: error instanceof Error ? error.message : 'Unknown error',
          loading: false
        };
      }
    })
  );

  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    }

    // This shouldn't happen since we catch errors above, but handle it anyway
    return {
      serviceId: endpoints[index].serviceId,
      serviceName: endpoints[index].serviceName,
      status: 'unknown' as const,
      lastChecked: new Date().toISOString(),
      error: 'Failed to fetch health status',
      loading: false
    };
  });
}

/**
 * Get color class for health status indicator
 */
export function getStatusColor(status: HealthStatus | 'unknown'): string {
  switch (status) {
    case 'pass':
      return 'bg-green-500';
    case 'warn':
      return 'bg-yellow-500';
    case 'fail':
      return 'bg-red-500';
    case 'unknown':
    default:
      return 'bg-gray-400';
  }
}

/**
 * Get text color class for health status
 */
export function getStatusTextColor(status: HealthStatus | 'unknown'): string {
  switch (status) {
    case 'pass':
      return 'text-green-700';
    case 'warn':
      return 'text-yellow-700';
    case 'fail':
      return 'text-red-700';
    case 'unknown':
    default:
      return 'text-gray-700';
  }
}

/**
 * Get human-readable status text
 */
export function getStatusText(status: HealthStatus | 'unknown'): string {
  switch (status) {
    case 'pass':
      return 'Healthy';
    case 'warn':
      return 'Degraded';
    case 'fail':
      return 'Unhealthy';
    case 'unknown':
    default:
      return 'Unknown';
  }
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);

    if (seconds < 60) {
      return `${seconds}s ago`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }

    return date.toLocaleString();
  } catch {
    return 'Invalid date';
  }
}
